import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter, Link } from "react-router-dom";
import Select from "react-select";
import { CircularProgress } from "@material-ui/core";
import ConfirmDialog from "../../components/ConfirmDialog";
import Icon from "../../components/Icon";
import Images from "../../utils/styles/images";
import {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
} from "./redux/actions";
import { AUTO_REFRESH_MS, SEND_STATUS_LABEL } from "./api";
import "./style.scss";

// Status filter — server-side (sent as sendStatus; "ALL" means the filter is off).
const STATUS_OPTIONS = [
  { value: "ALL", label: "All statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "PENDING", label: "Pending" },
  { value: "SENDING", label: "Sending" },
  { value: "SENT", label: "Sent" },
  { value: "PARTIAL", label: "Partially sent" },
  { value: "FAILED", label: "Failed" },
  { value: "CANCELLED", label: "Cancelled" }
];

// Content type filter — client-side (the list API has no content-type parameter).
const CONTENT_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "GENERAL", label: "General" },
  { value: "PROMO", label: "Promo" },
  { value: "URGENT", label: "Urgent" }
];

// CTA (actionType) filter — client-side, same as the content-type one.
const CTA_FILTER_OPTIONS = [
  { value: "", label: "All CTA" },
  { value: "NONE", label: "None" },
  { value: "URL", label: "Link" },
  { value: "DEEPLINK", label: "In-app" },
  { value: "SURVEY", label: "Survey" }
];

/*
 * The two tabs: campaigns an operator composed (Manual) and notifications raised by a backend
 * event (Automatic). Filtered by the list API's `origin`, so each tab pages and counts on its own.
 * The tab is kept in the URL (?source=automatic) so a refresh or a shared link reopens it.
 * Automatic is view-only: no Create new, no row actions.
 */
const TABS = [
  { value: "manual", label: "Manual", origin: "ADMIN" },
  { value: "automatic", label: "Automatic", origin: "AUTOMATED" }
];

// be-notification's NotificationEventType, labelled for the Automatic tab's Event column / filter.
const EVENT_LABEL = {
  ACCOUNT_OPENING_PROGRESS: "Account opening progress",
  ACCOUNT_APPROVED: "Account approved",
  ACCOUNT_REJECTED_ID: "Account rejected (ID)",
  MF_TRANSACTION_STATUS: "MF transaction status",
  PVD_TRANSACTION_STATUS: "PVD transaction status",
  PVD_REGISTRATION_SUCCESS: "PVD registration success",
  NDID_ABANDONED: "NDID abandoned",
  CSAT_REQUEST: "CSAT request",
  KYC_UPDATED: "KYC updated",
  PASSWORD_CHANGED: "Password changed",
  NEW_DEVICE_LOGIN: "New device login",
  MOBILE_CHANGED: "Mobile changed",
  CRS_FATCA_UPDATED: "CRS / FATCA updated",
  RSP_STATUS: "RSP status",
  REDEMPTION_BANK_STATUS: "Redemption bank status",
  SUITABILITY_UPDATED: "Suitability updated",
  SOFT_ONBOARD_SUCCESS: "Soft onboard success"
};

const EVENT_FILTER_OPTIONS = [
  { value: "", label: "All events" },
  ...Object.keys(EVENT_LABEL).map(value => ({ value, label: EVENT_LABEL[value] }))
];

const PAGE_SIZE = 20;

const CONTENT_TYPE_LABEL = {
  GENERAL: "General",
  PROMO: "Promo",
  URGENT: "Urgent"
};

/*
 * CTA = the "Button in the message" (actionType) a notification carries. Shown as plain text —
 * a short label per kind, or a dash for NONE. The destination (actionLink) stays as a tooltip.
 * For a survey, the survey code (actionLink) is read to name the kind: "Survey - CSAT" / "- NPS".
 */
const CTA_LABEL = {
  URL: "Link",
  DEEPLINK: "In-app",
  SURVEY: "Survey"
};

const ctaText = notification => {
  const type = notification.actionType;
  if (!type || type === "NONE") {
    return "—";
  }
  if (type === "SURVEY") {
    const link = notification.actionLink || "";
    const kind = /csat/i.test(link) ? "CSAT" : /nps/i.test(link) ? "NPS" : "";
    return kind ? `Survey - ${kind}` : "Survey";
  }
  return CTA_LABEL[type] || type;
};

// The Consent page's View action (eye icon, underlined blue text), for a row that can only be
// viewed: every automatic notification, and a manual one once it was sent. Opens it read-only.
const viewLink = notification => (
  <Link
    className="nl-action-view"
    to={`/PushNoti_Compose?id=${notification.id}`}
    aria-label={`View ${notification.header}`}
  >
    <img src={Images.fundListIcon.view} alt="" />
    View
  </Link>
);

/*
 * Each confirm maps to a short sentence-case title, an optional explanatory body, and the label on
 * the confirming (right) button so it says what it does rather than a generic "Yes". The dismissing
 * (left) button is "Cancel" by default, overridden where that word would clash with the action
 * itself (cancelling a scheduled send). This feeds the shared ConfirmDialog, so every prompt here
 * matches the "Discard unsaved changes?" card — short heading, body line, left/right buttons.
 */
const CONFIRM_META = {
  SEND: notification => ({
    title: `Send "${notification.header}" now?`,
    body: `It is sent to the ${notification.userScope} customer group right now and cannot be unsent.`,
    confirm: "Send now"
  }),
  DELETE: notification => ({
    title: `Delete "${notification.header}"?`,
    body: "This removes the notification from the list. This cannot be undone.",
    confirm: "Delete"
  }),
  // A scheduled send that has not gone out yet: cancelling keeps the record but stops the send.
  CANCEL: notification => ({
    title: `Cancel the scheduled send of "${notification.header}"?`,
    body: "It stays in the list as cancelled and will not be sent.",
    confirm: "Cancel send",
    dismiss: "Keep scheduled"
  }),
  RESUME: notification => ({
    title: `Resume the scheduled send of "${notification.header}"?`,
    body: "It sends at its scheduled time (or now if that time has passed).",
    confirm: "Resume"
  })
};

const formatDate = value => {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString("th-TH");
};

const formatCount = value => (value == null ? "—" : Number(value).toLocaleString());

/*
 * An admin's username (their Okta login) from the backend audit trail, or "" when the value
 * names no one an operator would recognise: event-raised rows are recorded as "system", and
 * rows saved before the login was recorded hold the admin's customerId GUID instead.
 */
const SYSTEM_ACTOR = "system";
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const adminUsername = value => {
  const actor = (value || "").trim();
  return actor && actor !== SYSTEM_ACTOR && !GUID_PATTERN.test(actor) ? actor : "";
};

/*
 * A campaign that has gone out can no longer be edited or deleted; it is history.
 *
 * The test is the send time, not the send status: a campaign whose pushes all failed
 * still reached every recipient's inbox and may already have been read. This mirrors
 * the backend guard, which refuses the edit either way.
 */
const isDelivered = notification => Boolean(notification.timeSent);

/*
 * A campaign that is queued to go out but has not been sent yet: scheduled (a Send date in
 * the future) or otherwise pending. These are the ones an operator can still cancel.
 */
const isScheduledPending = notification =>
  !isDelivered(notification) && notification.sendStatus === "PENDING";

/*
 * A scheduled send the operator has cancelled. Cancelling flips the campaign to INACTIVE,
 * which the scheduler never picks up (it only dispatches ACTIVE + PENDING rows), so the
 * record and its Send date stay while the send is stopped. Resuming turns it back to ACTIVE.
 */
const isCancelledSchedule = notification =>
  isScheduledPending(notification) && notification.status !== "ACTIVE";

/*
 * The send status an operator sees, folding the cancelled-schedule case (INACTIVE + PENDING)
 * into CANCELLED. Drives both the badge label and its colour class (s-draft, s-sent, …).
 */
const effectiveStatus = notification =>
  isCancelledSchedule(notification) ? "CANCELLED" : notification.sendStatus;

function NotificationList(props) {
  const {
    REQNotificationList,
    SENDxNotification,
    DELxNotification,
    SETxNotificationStatus,
    CleanUpNotificationList,
    REQStrNotificationList,
    NotificationList,
    Pagination,
    errorMessage,
    history,
    location
  } = props;

  const tab = TABS.find(item => item.value === new URLSearchParams(location.search).get("source")) || TABS[0];
  const automaticTab = tab.value === "automatic";

  const [oSearch, SETxSearch] = useState("");
  const [oSubmittedSearch, SETxSubmittedSearch] = useState("");
  const [oFilter, SETxFilter] = useState("ALL");
  const [oPage, SETxPage] = useState(1);
  const [oConfirm, SETxConfirm] = useState(null);
  // The row whose actions (⋮) menu is open, or null.
  const [oMenu, SETxMenu] = useState(null);
  // The row menu opens upward for a row near the bottom, so the scrolling table box never clips it.
  const [oMenuUp, SETxMenuUp] = useState(false);
  const toggleMenu = (event, id) => {
    if (oMenu === id) {
      SETxMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    SETxMenuUp(window.innerHeight - rect.bottom < 210);
    SETxMenu(id);
  };
  // Event filter (Automatic tab) - server-side, like status and search.
  const [oEventType, SETxEventType] = useState("");
  // Client-side filters (the list API has no content-type / CTA / date parameters).
  const [oContentType, SETxContentType] = useState("");
  const [oCta, SETxCta] = useState("");
  // Created by (Manual tab) - the admin's username, matched on the rows of the current page.
  const [oCreator, SETxCreator] = useState("");
  const [oDateFrom, SETxDateFrom] = useState("");
  const [oDateTo, SETxDateTo] = useState("");

  const filters = {
    search: oSubmittedSearch,
    sendStatus: oFilter,
    origin: tab.origin,
    eventType: automaticTab ? oEventType : "",
    page: oPage,
    limit: PAGE_SIZE
  };

  useEffect(() => {
    REQNotificationList(filters);
    return () => {
      CleanUpNotificationList();
    };
  }, [oSubmittedSearch, oFilter, oPage, tab.value, oEventType]);

  // Switching tab starts it from page 1 and drops the filters only the other tab has.
  const onTab = value => {
    if (value === tab.value) {
      return;
    }
    SETxMenu(null);
    SETxPage(1);
    SETxEventType("");
    SETxContentType("");
    SETxCta("");
    SETxCreator("");
    history.replace(value === "automatic" ? `${location.pathname}?source=automatic` : location.pathname);
  };

  /*
   * Notifications raised by backend events arrive without anyone on this screen doing anything,
   * so the page re-reads itself every AUTO_REFRESH_MS. Paused while a load is running (the timer
   * restarts once it ends) and while the browser tab is hidden.
   */
  useEffect(() => {
    if (REQStrNotificationList) {
      return undefined;
    }
    const timerId = setInterval(() => {
      if (!document.hidden) {
        REQNotificationList({ ...filters, silent: true });
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timerId);
  }, [oSubmittedSearch, oFilter, oPage, tab.value, oEventType, REQStrNotificationList]);

  // Close the row actions menu on an outside click or Escape.
  useEffect(() => {
    if (!oMenu) {
      return undefined;
    }
    const close = event => {
      if (!event.target.closest || !event.target.closest(".nl-menu-wrap")) {
        SETxMenu(null);
      }
    };
    const esc = event => {
      if (event.key === "Escape") {
        SETxMenu(null);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [oMenu]);

  const onSearch = event => {
    event.preventDefault();
    SETxPage(1);
    SETxSubmittedSearch(oSearch.trim());
  };

  // Reset every filter (search box, the dropdowns, and the date range) back to its default.
  const onClearFilters = () => {
    SETxSearch("");
    SETxSubmittedSearch("");
    SETxFilter("ALL");
    SETxEventType("");
    SETxContentType("");
    SETxCta("");
    SETxCreator("");
    SETxDateFrom("");
    SETxDateTo("");
    SETxPage(1);
  };

  const onConfirm = () => {
    if (!oConfirm) {
      return;
    }
    if (oConfirm.action === "SEND") {
      SENDxNotification({ id: oConfirm.notification.id, filters });
    }
    if (oConfirm.action === "DELETE") {
      DELxNotification({ id: oConfirm.notification.id, filters });
    }
    // Cancel / resume a scheduled send: the same status flip, kept apart so the confirm copy fits.
    if (oConfirm.action === "CANCEL") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "INACTIVE", filters });
    }
    if (oConfirm.action === "RESUME") {
      SETxNotificationStatus({ id: oConfirm.notification.id, status: "ACTIVE", filters });
    }
    SETxConfirm(null);
  };

  // Open the confirm dialog for a row action and close the menu.
  const ask = (action, notification) => {
    SETxMenu(null);
    SETxConfirm({ action, notification });
  };

  const rows = NotificationList || [];

  // Content type, CTA, created by and date range are applied here, on the current page, because the list API
  // filters only by tab (origin), event, status and search, which come filtered from the backend.
  const visibleRows = rows.filter(notification => {
    if (oContentType && notification.contentType !== oContentType) {
      return false;
    }
    // A row with no action recorded is treated as NONE, so the "None" filter catches old rows.
    if (oCta && (notification.actionType || "NONE") !== oCta) {
      return false;
    }
    if (oCreator && adminUsername(notification.createdBy) !== oCreator) {
      return false;
    }
    const day = String(notification.timeSent || notification.scheduledAt || "").slice(0, 10);
    if (oDateFrom && (!day || day < oDateFrom)) {
      return false;
    }
    if (oDateTo && (!day || day > oDateTo)) {
      return false;
    }
    return true;
  });

  const hasFilters =
    oSubmittedSearch ||
    oFilter !== "ALL" ||
    oEventType ||
    oContentType ||
    oCta ||
    oCreator ||
    oDateFrom ||
    oDateTo;

  // The admins behind the loaded rows, for the Created by filter. The one chosen stays listed even
  // when the page has none of theirs, so the dropdown still shows what is selected.
  const creatorNames = Array.from(
    new Set([...rows.map(notification => adminUsername(notification.createdBy)), oCreator].filter(Boolean))
  ).sort();
  const CREATOR_FILTER_OPTIONS = [
    { value: "", label: "All admins" },
    ...creatorNames.map(name => ({ value: name, label: name }))
  ];

  const totalPages = (Pagination && Pagination.totalPages) || 0;
  const total = (Pagination && Pagination.total) || 0;
  const rangeStart = total === 0 ? 0 : (oPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(oPage * PAGE_SIZE, total);

  const statusBadge = notification => {
    const status = effectiveStatus(notification);
    const label = SEND_STATUS_LABEL[status] || status;
    const className = `nl-status-badge s-${(status || "").toLowerCase()}`;
    // A delivered campaign's status opens its delivery log: who it reached and who it did not.
    if (isDelivered(notification)) {
      return (
        <Link
          className={className}
          title="View delivery log"
          to={`/Notification_DeliveryLog?id=${notification.id}`}
        >
          {label}
        </Link>
      );
    }
    return <span className={className}>{label}</span>;
  };

  return (
    <Fragment>
      {REQStrNotificationList ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Loading notifications...</p>
        </div>
      ) : null}

      {/*
        * No heading: the breadcrumb already names the page. "Create new" sits on the breadcrumb's
        * line at the right, where CANCEL / SAVE DRAFT are on the compose page it opens.
        */}
      <div className="noti-list-container noti-list-container-actions-only">
        <div className="noti-list-topbar noti-list-topbar-actions-only">
          {/* Automatic notifications are raised by events, never composed here. */}
          {automaticTab ? null : (
            <div className="noti-list-actions">
              <Link className="noti-list-create" to="/PushNoti_Compose">
                <Icon name="plus" size={18} />
                Create new
              </Link>
            </div>
          )}
        </div>

        {errorMessage ? <div className="noti-list-error">{errorMessage}</div> : null}

        <div className="nl-filter-row">
          <div className="nl-filter-field nl-filter-field-wide">
            <span className="nl-filter-label">Search</span>
            <form className="nl-search" onSubmit={onSearch}>
              <Icon name="search" size={18} />
              <input
                type="search"
                // The backend also matches the Event, Type, CTA and Status columns by what they show,
                // and Created by; Event is on the Automatic tab only, Created by on the Manual tab.
                placeholder={
                  automaticTab
                    ? "Search by name, title, event, type, CTA, status…"
                    : "Search by name, title, type, CTA, status, created by…"
                }
                value={oSearch}
                onChange={event => SETxSearch(event.target.value)}
              />
            </form>
          </div>
          {automaticTab ? (
            <div className="nl-filter-field nl-filter-field-event">
              <span className="nl-filter-label">Event</span>
              <Select
                className="basic-single"
                classNamePrefix="select"
                aria-label="Filter by event"
                isSearchable={false}
                options={EVENT_FILTER_OPTIONS}
                value={EVENT_FILTER_OPTIONS.find(option => option.value === oEventType) || null}
                onChange={option => {
                  SETxPage(1);
                  SETxEventType(option ? option.value : "");
                }}
              />
            </div>
          ) : (
            <Fragment>
              <div className="nl-filter-field">
                <span className="nl-filter-label">Type</span>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  aria-label="Filter by type"
                  isSearchable={false}
                  options={CONTENT_TYPE_OPTIONS}
                  value={CONTENT_TYPE_OPTIONS.find(option => option.value === oContentType) || null}
                  onChange={option => SETxContentType(option ? option.value : "")}
                />
              </div>
              <div className="nl-filter-field">
                <span className="nl-filter-label">CTA</span>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  aria-label="Filter by CTA"
                  isSearchable={false}
                  options={CTA_FILTER_OPTIONS}
                  value={CTA_FILTER_OPTIONS.find(option => option.value === oCta) || null}
                  onChange={option => SETxCta(option ? option.value : "")}
                />
              </div>
            </Fragment>
          )}
          <div className="nl-filter-field">
            <span className="nl-filter-label">Status</span>
            <Select
              className="basic-single"
              classNamePrefix="select"
              aria-label="Filter by status"
              isSearchable={false}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find(option => option.value === oFilter) || null}
              onChange={option => {
                SETxPage(1);
                SETxFilter(option ? option.value : "ALL");
              }}
            />
          </div>
          {automaticTab ? null : (
            <div className="nl-filter-field nl-filter-field-creator">
              <span className="nl-filter-label">Created by</span>
              <Select
                className="basic-single"
                classNamePrefix="select"
                aria-label="Filter by created by"
                options={CREATOR_FILTER_OPTIONS}
                value={CREATOR_FILTER_OPTIONS.find(option => option.value === oCreator) || null}
                onChange={option => SETxCreator(option ? option.value : "")}
              />
            </div>
          )}
          <div className="nl-filter-field nl-filter-field-date">
            <span className="nl-filter-label">From</span>
            <input
              className="nl-date-input"
              type="date"
              aria-label="From date"
              value={oDateFrom}
              onChange={event => SETxDateFrom(event.target.value)}
            />
          </div>
          <div className="nl-filter-field nl-filter-field-date">
            <span className="nl-filter-label">To</span>
            <input
              className="nl-date-input"
              type="date"
              aria-label="To date"
              min={oDateFrom}
              value={oDateTo}
              onChange={event => SETxDateTo(event.target.value)}
            />
          </div>
          <div className="nl-filter-actions">
            <button type="button" className="nl-clear" onClick={onClearFilters} disabled={!hasFilters}>
              <Icon name="close" size={18} />
              Clear filters
            </button>
          </div>
        </div>

        <div className="nl-tabs" role="tablist" aria-label="Notification source">
          {TABS.map(item => (
            <button
              key={item.value}
              type="button"
              role="tab"
              aria-selected={item.value === tab.value}
              className={item.value === tab.value ? "nl-tab active" : "nl-tab"}
              onClick={() => onTab(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* The open tab's white panel: the table and pager sit on it, joined to the tab. */}
        <div className="nl-panel" role="tabpanel">
          <div className="nl-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  {automaticTab ? <th>Event</th> : null}
                  <th>Type</th>
                  <th>CTA</th>
                  <th>Notification</th>
                  <th>Time sent</th>
                  <th className="nl-num">Reach</th>
                  <th className="nl-num">View</th>
                  <th className="nl-num">Click</th>
                  <th className="nl-status-cell">Status</th>
                  {/* Automatic rows are all "system": no audit columns. */}
                  {automaticTab ? null : (
                    <Fragment>
                      <th>Created by</th>
                      <th>Updated</th>
                    </Fragment>
                  )}
                  <th className="nl-actions-head">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(notification => {
                  const delivered = isDelivered(notification);
                  const scheduled = isScheduledPending(notification);
                  const cancelled = isCancelledSchedule(notification);
                  const creator = adminUsername(notification.createdBy);
                  return (
                    <tr key={notification.id}>
                      <td className="nl-code" title={notification.code}>
                        {notification.code || "—"}
                      </td>
                      {automaticTab ? (
                        <td className="nl-event-cell" title={notification.eventType || ""}>
                          {EVENT_LABEL[notification.eventType] || notification.eventType || "—"}
                        </td>
                      ) : null}
                      <td className="nl-type-cell">
                        {CONTENT_TYPE_LABEL[notification.contentType] || notification.contentType}
                      </td>
                      <td className="nl-cta-cell" title={notification.actionLink || ""}>
                        {ctaText(notification)}
                      </td>
                      <td className="nl-noti-cell">
                        {/* Opens read-only for a sent or an automatic notification. */}
                        <Link className="nl-name-btn" to={`/PushNoti_Compose?id=${notification.id}`}>
                          {notification.header}
                        </Link>
                        {notification.content ? (
                          <div className="nl-noti-body">{notification.content}</div>
                        ) : null}
                        <div className="nl-sub">
                          {[
                            // The Automatic tab shows the event in its own column.
                            automaticTab ? null : notification.eventType,
                            notification.name,
                            notification.notificationType,
                            notification.userScope
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </td>
                      <td className="nl-date">
                        {formatDate(notification.timeSent || notification.scheduledAt)}
                      </td>
                      <td className="nl-num">{formatCount(notification.reach)}</td>
                      <td className="nl-num">{formatCount(notification.view)}</td>
                      <td className="nl-num">{formatCount(notification.click)}</td>
                      <td className="nl-status-cell">{statusBadge(notification)}</td>
                      {automaticTab ? (
                        // Automatic notifications are view-only.
                        <td className="nl-menu-cell">{viewLink(notification)}</td>
                      ) : (
                        <Fragment>
                          <td className="nl-actor-cell" title={creator}>
                            {creator || "—"}
                          </td>
                          <td className="nl-date">{formatDate(notification.updatedAt)}</td>
                          {/* A sent campaign is history and can only be viewed (no hide from inbox),
                              so it gets the plain View link; one not sent yet keeps the menu. */}
                          {delivered ? (
                            <td className="nl-menu-cell">{viewLink(notification)}</td>
                          ) : (
                            <td className="nl-menu-cell">
                              <div className="nl-menu-wrap">
                                <button
                                  type="button"
                                  className="nl-kebab"
                                  aria-label={`Actions for ${notification.header}`}
                                  aria-haspopup="menu"
                                  aria-expanded={oMenu === notification.id}
                                  onClick={event => toggleMenu(event, notification.id)}
                                >
                                  ⋮
                                </button>
                                {oMenu === notification.id ? (
                                  <div className={oMenuUp ? "nl-row-menu up" : "nl-row-menu"} role="menu">
                                    <Link
                                      role="menuitem"
                                      to={`/PushNoti_Compose?id=${notification.id}`}
                                      onClick={() => SETxMenu(null)}
                                    >
                                      Edit
                                    </Link>
                                    {cancelled ? (
                                      <button type="button" role="menuitem" onClick={() => ask("RESUME", notification)}>
                                        Resume
                                      </button>
                                    ) : scheduled ? (
                                      <button type="button" role="menuitem" onClick={() => ask("CANCEL", notification)}>
                                        Cancel
                                      </button>
                                    ) : notification.status === "ACTIVE" ? (
                                      <button type="button" role="menuitem" onClick={() => ask("SEND", notification)}>
                                        Send now
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      role="menuitem"
                                      className="nl-danger"
                                      onClick={() => ask("DELETE", notification)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          )}
                        </Fragment>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!REQStrNotificationList && visibleRows.length === 0 ? (
              <div className="nl-empty">
                <img src={Images.noData.board} alt="" />
                <p className="nl-empty-title">
                  {hasFilters
                    ? "No results match your filters"
                    : automaticTab
                    ? "No automatic notifications yet"
                    : "No notifications yet"}
                </p>
                <p className="nl-empty-sub">
                  {hasFilters
                    ? "Try another search or clear the filters."
                    : automaticTab
                    ? "They appear here once a customer event raises one."
                    : "Use “Create new” to compose one."}
                </p>
              </div>
            ) : null}
          </div>

          {total > 0 ? (
            <div className="nl-pager">
              <span className="nl-pager-info">
                {rangeStart}–{rangeEnd} of {total}
              </span>
              <div className="nl-pager-btns">
                <button
                  type="button"
                  className="nl-pager-btn"
                  disabled={oPage <= 1}
                  onClick={() => SETxPage(oPage - 1)}
                >
                  ‹ Prev
                </button>
                <span className="nl-pager-page">
                  {(Pagination && Pagination.page) || oPage} / {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="nl-pager-btn"
                  disabled={oPage >= totalPages}
                  onClick={() => SETxPage(oPage + 1)}
                >
                  Next ›
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {oConfirm ? (
        (() => {
          const meta = CONFIRM_META[oConfirm.action](oConfirm.notification);
          return (
            <ConfirmDialog
              title={meta.title}
              labelId="noti-confirm-title"
              onDismiss={() => SETxConfirm(null)}
              actions={[
                { label: meta.dismiss || "Cancel", autoFocus: true, onClick: () => SETxConfirm(null) },
                { label: meta.confirm, primary: true, onClick: onConfirm }
              ]}
            >
              {meta.body}
            </ConfirmDialog>
          );
        })()
      ) : null}
    </Fragment>
  );
}

const mapStateToProps = state => ({
  REQStrNotificationList: state.notificationListReducer.REQStrNotificationList,
  NotificationList: state.notificationListReducer.NotificationList,
  Pagination: state.notificationListReducer.Pagination,
  errorMessage: state.notificationListReducer.errorMessage
});

const mapDispatchToProps = {
  REQNotificationList,
  SENDxNotification,
  DELxNotification,
  SETxNotificationStatus,
  CleanUpNotificationList
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(NotificationList);
